/**
 * Race Results Utility Functions
 * 
 * Formatting and parsing utilities for race results
 * - Position formatting with ordinal suffixes
 * - Time formatting (MM:SS or HH:MM:SS)
 * - Time parsing (minutes + seconds to total seconds)
 * 
 * Requirements: 7.3, 7.6
 */

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
export function getOrdinalSuffix(n: number): string {
  const j = n % 10;
  const k = n % 100;
  
  if (j === 1 && k !== 11) {
    return 'st';
  }
  if (j === 2 && k !== 12) {
    return 'nd';
  }
  if (j === 3 && k !== 13) {
    return 'rd';
  }
  return 'th';
}

/**
 * Format position with ordinal suffix
 * 
 * @param position - Position number (1, 2, 3, etc.)
 * @param totalRacers - Optional total number of racers
 * @param dnf - Optional DNF flag (Did Not Finish)
 * @returns Formatted position string (e.g., "1st", "2nd / 25", "DNF")
 * 
 * Examples:
 * - formatPosition(1) => "1st"
 * - formatPosition(2, 25) => "2nd / 25"
 * - formatPosition(21) => "21st"
 * - formatPosition(1, undefined, true) => "DNF"
 */
export function formatPosition(position: number, totalRacers?: number, dnf?: boolean): string {
  // If DNF flag is set, return "DNF" regardless of position
  if (dnf) {
    return 'DNF';
  }
  
  const suffix = getOrdinalSuffix(position);
  const positionStr = `${position}${suffix}`;
  
  if (totalRacers && totalRacers > 0) {
    return `${positionStr} / ${totalRacers}`;
  }
  
  return positionStr;
}

/**
 * Format time in seconds to MM:SS or HH:MM:SS format
 * 
 * @param seconds - Time in seconds
 * @returns Formatted time string
 * 
 * Examples:
 * - formatTime(65) => "1:05"
 * - formatTime(3665) => "1:01:05"
 * - formatTime(45) => "0:45"
 */
export function formatTime(seconds: number): string {
  if (!seconds || seconds < 0) {
    return '0:00';
  }
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  // Format with leading zeros
  const minutesStr = minutes.toString().padStart(2, '0');
  const secsStr = secs.toString().padStart(2, '0');
  
  if (hours > 0) {
    return `${hours}:${minutesStr}:${secsStr}`;
  }
  
  return `${minutes}:${secsStr}`;
}

/**
 * Parse time input (minutes and seconds) to total seconds
 * 
 * @param minutes - Minutes component
 * @param seconds - Seconds component
 * @returns Total time in seconds, or undefined if invalid
 * 
 * Examples:
 * - parseTimeInput(2, 30) => 150
 * - parseTimeInput(0, 45) => 45
 * - parseTimeInput(undefined, undefined) => undefined
 */
export function parseTimeInput(minutes?: number, seconds?: number): number | undefined {
  // If both are undefined or null, return undefined
  if (minutes === undefined && seconds === undefined) {
    return undefined;
  }
  
  const m = minutes || 0;
  const s = seconds || 0;
  
  // Validate inputs
  if (m < 0 || s < 0 || s >= 60) {
    return undefined;
  }
  
  return m * 60 + s;
}

/**
 * Parse time string in HH:MM:SS or MM:SS format to total seconds
 * 
 * @param timeString - Time string in HH:MM:SS or MM:SS format
 * @returns Total time in seconds, or undefined if invalid
 * 
 * Examples:
 * - parseTimeString("1:30:45") => 5445
 * - parseTimeString("2:30") => 150
 * - parseTimeString("45") => 45
 * - parseTimeString("") => undefined
 */
export function parseTimeString(timeString?: string): number | undefined {
  if (!timeString || timeString.trim() === '') {
    return undefined;
  }
  
  const parts = timeString.trim().split(':').map(p => parseInt(p.trim(), 10));
  
  // Filter out NaN values
  const validParts = parts.filter(p => !isNaN(p) && p >= 0);
  
  if (validParts.length === 0) {
    return undefined;
  }
  
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  
  if (validParts.length === 1) {
    // Just seconds: "45"
    seconds = validParts[0];
  } else if (validParts.length === 2) {
    // MM:SS format: "2:30"
    minutes = validParts[0];
    seconds = validParts[1];
  } else if (validParts.length === 3) {
    // HH:MM:SS format: "1:30:45"
    hours = validParts[0];
    minutes = validParts[1];
    seconds = validParts[2];
  } else {
    return undefined;
  }
  
  // Validate ranges
  if (seconds >= 60 || minutes >= 60) {
    return undefined;
  }
  
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Format seconds to HH:MM:SS or MM:SS string for input field
 * 
 * @param totalSeconds - Total time in seconds
 * @returns Formatted time string for input
 * 
 * Examples:
 * - formatTimeForInput(5445) => "1:30:45"
 * - formatTimeForInput(150) => "2:30"
 * - formatTimeForInput(45) => "0:45"
 */
export function formatTimeForInput(totalSeconds?: number): string {
  if (totalSeconds === undefined || totalSeconds === null || totalSeconds < 0) {
    return '';
  }
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  
  const minutesStr = minutes.toString().padStart(2, '0');
  const secondsStr = seconds.toString().padStart(2, '0');
  
  if (hours > 0) {
    return `${hours}:${minutesStr}:${secondsStr}`;
  }
  
  return `${minutes}:${secondsStr}`;
}

/**
 * Convert total seconds to minutes and seconds components
 * 
 * @param totalSeconds - Total time in seconds
 * @returns Object with minutes and seconds, or undefined if invalid
 * 
 * Examples:
 * - secondsToMinutesAndSeconds(150) => { minutes: 2, seconds: 30 }
 * - secondsToMinutesAndSeconds(45) => { minutes: 0, seconds: 45 }
 */
export function secondsToMinutesAndSeconds(totalSeconds?: number): { minutes: number; seconds: number } | undefined {
  if (totalSeconds === undefined || totalSeconds === null || totalSeconds < 0) {
    return undefined;
  }
  
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  
  return { minutes, seconds };
}

